import { useEffect, useRef } from 'react';

const normalizeDevice = (device) => ({
  deviceId: device?.deviceId || '',
  label: device?.label || '',
});

const mergeDevices = (sessionDevices, systemDevices) => {
  const byId = new Map();

  (sessionDevices || []).forEach((device) => {
    const normalized = normalizeDevice(device);
    if (!normalized.deviceId) {
      return;
    }
    byId.set(normalized.deviceId, normalized);
  });

  (systemDevices || []).forEach((device) => {
    const normalized = normalizeDevice(device);
    if (!normalized.deviceId) {
      return;
    }

    if (!byId.has(normalized.deviceId)) {
      byId.set(normalized.deviceId, normalized);
      return;
    }

    const existing = byId.get(normalized.deviceId);
    if (!existing.label && normalized.label) {
      byId.set(normalized.deviceId, { ...existing, label: normalized.label });
    }
  });

  return Array.from(byId.values());
};

const useAudioOutputSync = ({
  meetingManager,
  activeDeviceId,
  setActiveDeviceId,
  onDeviceSelected,
  setMenuDevices,
  initialDeviceId = '',
  autoSelectNewDevices = true,
}) => {
  const knownIdsRef = useRef(new Set());
  const activeDeviceIdRef = useRef(activeDeviceId || '');
  const applyingRef = useRef(false);
  const pendingDeviceIdRef = useRef('');
  const didInitialSyncRef = useRef(false);

  useEffect(() => {
    activeDeviceIdRef.current = activeDeviceId || '';
  }, [activeDeviceId]);

  const applyOutputDevice = async (deviceId) => {
    const currentActiveDeviceId = activeDeviceIdRef.current;
    if (!deviceId || deviceId === currentActiveDeviceId) {
      return;
    }

    if (applyingRef.current) {
      pendingDeviceIdRef.current = deviceId;
      return;
    }

    applyingRef.current = true;
    try {
      const av = meetingManager?.meetingSession?.audioVideo;
      if (!av) {
        return;
      }

      if (typeof meetingManager?.startAudioOutputDevice === 'function') {
        await meetingManager.startAudioOutputDevice(deviceId);
      } else if (typeof av.chooseAudioOutputDevice === 'function') {
        await av.chooseAudioOutputDevice(deviceId);
      }

      activeDeviceIdRef.current = deviceId;
      setActiveDeviceId(deviceId);
      if (typeof onDeviceSelected === 'function') {
        onDeviceSelected(deviceId);
      }
    } catch (_error) {
      // Non-fatal.
    } finally {
      applyingRef.current = false;
      const pendingDeviceId = pendingDeviceIdRef.current;
      pendingDeviceIdRef.current = '';
      if (pendingDeviceId && pendingDeviceId !== activeDeviceIdRef.current) {
        applyOutputDevice(pendingDeviceId);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const syncOutputDevices = async () => {
      try {
        const av = meetingManager?.meetingSession?.audioVideo;
        const sessionOutputs = (typeof av?.listAudioOutputDevices === 'function')
          ? await av.listAudioOutputDevices()
          : [];

        const canEnumerate = typeof navigator !== 'undefined' && navigator.mediaDevices?.enumerateDevices;
        if (!canEnumerate) {
          const merged = mergeDevices(sessionOutputs, []);
          setMenuDevices(merged);
          return;
        }

        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const systemOutputs = (allDevices || []).filter((device) => device.kind === 'audiooutput');

        if (cancelled) {
          return;
        }

        const mergedDevices = mergeDevices(sessionOutputs, systemOutputs);
        setMenuDevices(mergedDevices);

        if (!mergedDevices.length) {
          knownIdsRef.current = new Set();
          return;
        }

        const currentIds = new Set(mergedDevices.map((device) => device.deviceId));
        const previousIds = knownIdsRef.current;
        const currentActiveDeviceId = activeDeviceIdRef.current;
        const currentExists = !!(currentActiveDeviceId && currentIds.has(currentActiveDeviceId));

        const addedSystemDevices = didInitialSyncRef.current
          ? systemOutputs.filter((device) => !previousIds.has(device.deviceId))
          : [];

        knownIdsRef.current = currentIds;
        didInitialSyncRef.current = true;

        const fallbackId = mergedDevices[0]?.deviceId || '';
        const newSystemDeviceId = addedSystemDevices.length
          ? addedSystemDevices[addedSystemDevices.length - 1].deviceId
          : '';

        const nextDeviceId = (autoSelectNewDevices && newSystemDeviceId)
          ? newSystemDeviceId
          : (currentExists
            ? currentActiveDeviceId
            : (initialDeviceId && currentIds.has(initialDeviceId)
              ? initialDeviceId
              : fallbackId));

        if (!nextDeviceId || nextDeviceId === currentActiveDeviceId) {
          return;
        }

        await applyOutputDevice(nextDeviceId);
      } catch (_error) {
        // Non-fatal.
      }
    };

    syncOutputDevices();

    const handleDeviceChange = () => {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        timer = null;
        syncOutputDevices();
      }, 180);
    };

    if (typeof navigator !== 'undefined' && navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    }

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
      if (typeof navigator !== 'undefined' && navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      }
    };
  }, [
    autoSelectNewDevices,
    initialDeviceId,
    meetingManager,
    onDeviceSelected,
    setMenuDevices,
  ]);

  return { applyOutputDevice };
};

export default useAudioOutputSync;